import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, Search, RefreshCw, CheckCircle, XCircle, Clock, Trash2, AlertTriangle, ChevronDown, ChevronRight, Users, Eye } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

export default function EmailLogs() {
  const [groupedData, setGroupedData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [clearAllDialog, setClearAllDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    fetchData();
    fetchUserRole();
  }, []);

  const fetchUserRole = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role || '');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [logsResponse, statsResponse] = await Promise.all([
        api.get('/email-logs/by-family'),
        api.get('/email-logs/stats')
      ]);
      setGroupedData(logsResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      toast.error('Failed to fetch email logs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSingle = async (logId) => {
    setDeleting(true);
    try {
      await api.delete(`/email-logs/${logId}`);
      toast.success('Email log deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete email log');
    } finally {
      setDeleting(false);
      setDeleteDialog(null);
    }
  };

  const handleClearAll = async () => {
    setDeleting(true);
    try {
      const response = await api.delete('/email-logs');
      toast.success(`Cleared ${response.data.deleted_count} email log(s)`);
      setClearAllDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear email logs');
    } finally {
      setDeleting(false);
    }
  };

  const toggleFamilyExpanded = (familyId) => {
    setExpandedFamilies(prev => ({
      ...prev,
      [familyId]: !prev[familyId]
    }));
  };

  const expandAll = () => {
    const expanded = {};
    groupedData?.families?.forEach(f => {
      expanded[f.family_id] = true;
    });
    setExpandedFamilies(expanded);
  };

  const collapseAll = () => {
    setExpandedFamilies({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter families by search term
  const filteredFamilies = groupedData?.families?.filter(family => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      family.family_name?.toLowerCase().includes(search) ||
      family.emails?.some(email => 
        email.recipient_email?.toLowerCase().includes(search) ||
        email.subject?.toLowerCase().includes(search) ||
        email.template_name?.toLowerCase().includes(search)
      )
    );
  }) || [];

  const isAdmin = userRole === 'admin';
  const totalEmails = stats?.total || 0;

  return (
    <div className="min-h-screen bg-background" data-testid="email-logs-page">
      <Sidebar />
      <div className="ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary" data-testid="email-logs-title">Email Logs</h1>
              <p className="text-stone-500 mt-1">View all emails sent to families, grouped by family</p>
            </div>
            <div className="flex gap-2">
              {isAdmin && totalEmails > 0 && (
                <Button
                  onClick={() => setClearAllDialog(true)}
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              )}
              <Button
                onClick={fetchData}
                variant="outline"
                data-testid="refresh-logs-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <Card data-testid="stat-total-emails">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Total Emails</p>
                      <p className="text-2xl font-bold text-primary">{stats.total}</p>
                    </div>
                    <Mail className="w-10 h-10 text-primary opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="stat-sent-emails">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Successfully Sent</p>
                      <p className="text-2xl font-bold text-green-600">{stats.total_sent}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-green-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="stat-opened-emails">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Opened</p>
                      <p className="text-2xl font-bold text-blue-600">{stats.total_opened || 0}</p>
                      {stats.open_rate > 0 && (
                        <p className="text-xs text-blue-500 mt-1">{stats.open_rate}% open rate</p>
                      )}
                    </div>
                    <Eye className="w-10 h-10 text-blue-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="stat-failed-emails">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Failed</p>
                      <p className="text-2xl font-bold text-red-600">{stats.total_failed}</p>
                    </div>
                    <XCircle className="w-10 h-10 text-red-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="stat-families-count">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Families Contacted</p>
                      <p className="text-2xl font-bold text-primary">{groupedData?.total_families || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-primary opacity-20" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* By Template Stats */}
          {stats?.by_template && Object.keys(stats.by_template).length > 0 && (
            <Card className="mb-8" data-testid="template-breakdown">
              <CardHeader>
                <CardTitle className="text-lg">Emails by Template</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.by_template).map(([template, count]) => (
                    <Badge key={template} variant="secondary" className="text-sm px-3 py-1">
                      {template}: <span className="font-bold ml-1">{count}</span>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search and Controls */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex-1 w-full md:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      placeholder="Search by family name, email, or subject..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={expandAll}>
                    Expand All
                  </Button>
                  <Button variant="outline" size="sm" onClick={collapseAll}>
                    Collapse All
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Logs Grouped by Family */}
          <Card data-testid="email-logs-grouped">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Email History by Family</span>
                <span className="text-sm font-normal text-stone-500">
                  {filteredFamilies.length} families
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-stone-500 mt-4">Loading email logs...</p>
                </div>
              ) : filteredFamilies.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">No email logs found</p>
                  <p className="text-sm text-stone-400 mt-1">
                    Email logs will appear here once emails are sent to families
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFamilies.map((family) => (
                    <Collapsible
                      key={family.family_id || 'unknown'}
                      open={expandedFamilies[family.family_id]}
                      onOpenChange={() => toggleFamilyExpanded(family.family_id)}
                    >
                      <div className="border border-stone-200 rounded-lg overflow-hidden">
                        <CollapsibleTrigger asChild>
                          <div 
                            className="flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 cursor-pointer"
                            data-testid={`family-group-${family.family_id}`}
                          >
                            <div className="flex items-center gap-3">
                              {expandedFamilies[family.family_id] ? (
                                <ChevronDown className="w-5 h-5 text-stone-500" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-stone-500" />
                              )}
                              <Users className="w-5 h-5 text-primary" />
                              <div>
                                <span className="font-semibold text-primary">{family.family_name}</span>
                                <span className="text-sm text-stone-500 ml-2">
                                  ({family.total_emails} email{family.total_emails !== 1 ? 's' : ''})
                                </span>
                              </div>
                            </div>
                            <div className="text-sm text-stone-500">
                              Last email: {formatShortDate(family.last_email_at)}
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="divide-y divide-stone-100">
                            {family.emails.map((email) => (
                              <div 
                                key={email.id} 
                                className="p-4 hover:bg-stone-50 flex items-center justify-between"
                                data-testid={`email-log-${email.id}`}
                              >
                                <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                                  <div className="col-span-2 text-sm text-stone-500">
                                    {formatShortDate(email.sent_at)}
                                  </div>
                                  <div className="col-span-2">
                                    <Badge variant="outline" className="text-xs">
                                      {email.template_name || email.template_id || 'Custom'}
                                    </Badge>
                                  </div>
                                  <div className="col-span-3">
                                    <span className="text-sm text-stone-700 truncate block" title={email.subject}>
                                      {email.subject}
                                    </span>
                                  </div>
                                  <div className="col-span-2">
                                    {email.status === 'sent' ? (
                                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Sent
                                      </Badge>
                                    ) : (
                                      <Badge variant="destructive">
                                        <XCircle className="w-3 h-3 mr-1" />
                                        Failed
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="col-span-1">
                                    {email.opened ? (
                                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" title={email.opened_at ? `Opened: ${formatShortDate(email.opened_at)}` : 'Opened'}>
                                        <Eye className="w-3 h-3 mr-1" />
                                        Opened
                                      </Badge>
                                    ) : (
                                      <span className="text-xs text-stone-400">-</span>
                                    )}
                                  </div>
                                  <div className="col-span-2 text-sm text-stone-500 truncate" title={email.sent_by_name}>
                                    {email.sent_by_name || '-'}
                                  </div>
                                </div>
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteDialog(email);
                                    }}
                                    data-testid={`delete-log-${email.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Single Log Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Delete Email Log
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this email log?
              <div className="mt-2 p-3 bg-stone-100 rounded text-sm">
                <p><strong>To:</strong> {deleteDialog?.recipient_email}</p>
                <p><strong>Subject:</strong> {deleteDialog?.subject}</p>
                <p><strong>Date:</strong> {formatDate(deleteDialog?.sent_at)}</p>
              </div>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteSingle(deleteDialog?.id)}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Dialog */}
      <Dialog open={clearAllDialog} onOpenChange={setClearAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Clear All Email Logs
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>all {totalEmails} email logs</strong>?
              <p className="mt-2 text-red-600 font-medium">This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearAllDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearAll}
              disabled={deleting}
              data-testid="confirm-clear-all-btn"
            >
              {deleting ? 'Clearing...' : 'Clear All Logs'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
